import { useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearErr, fetchReg } from "../../../store/authSlice";
import authAPI from "../../../services/authApi";
import { openRegisteredClubAdmin } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { subdomainFromClubName } from "../../../utils/clubSubdomain";
import { formatPhoneDisplay, normalizePhoneRu } from "../../../utils/phoneMask";
import { LEGAL_PUBLIC_ENABLED } from "../../../constants/legalPaths";
import { TRIAL_DAYS } from "../../../constants/trial";
import ConsentCheckbox from "../../Legal/ConsentCheckbox";
import { LegalDocLink } from "../../Legal/LegalDocLink";
import site from "./site.module.css";
import h from "./pages/home.module.css";

const STEPS = ["Название клуба", "Адрес клуба", "Аккаунт владельца", "Запуск"];

function hostLabel(sub) {
  const s = String(sub || "").toLowerCase().replace(/[^a-z0-9-]/g, "") || "club";
  try {
    return new URL(buildClinicPortalUrl(s, "/")).host;
  } catch {
    return `${s}.bay-sport.ru`;
  }
}

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  return payload.error || payload.message || fallback;
}

export default function InlineRegister() {
  const dispatch = useDispatch();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    clinicName: "",
    subdomain: "",
    fullName: "",
    login: "",
    phone: "",
    password: "",
  });
  const [subTouched, setSubTouched] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [consentPd, setConsentPd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [existingEmail, setExistingEmail] = useState("");

  const patch = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const valid =
    step === 1
      ? data.clinicName.trim().length >= 2
      : step === 2
        ? /^[a-z0-9-]{2,32}$/.test(data.subdomain)
        : step === 3
          ? /\S+@\S+\.\S+/.test(data.login) &&
            data.password.length >= 8 &&
            data.fullName.trim().length >= 2
          : !LEGAL_PUBLIC_ENABLED || (acceptTerms && consentPd);

  const go = (n) => {
    setError("");
    setStep(n);
  };

  const next = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    if (step === 3) {
      setBusy(true);
      try {
        const res = await authAPI.checkRegisterOwnerEmail(
          data.login.trim().toLowerCase(),
        );
        if (res?.exists) {
          setExistingEmail(data.login.trim().toLowerCase());
          return;
        }
      } catch {
        /* проверку повторит сервер при создании */
      } finally {
        setBusy(false);
      }
    }
    go(step + 1);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    const res = await dispatch(
      fetchReg({
        ...data,
        ...(LEGAL_PUBLIC_ENABLED ? { accept_terms: true, consent_pd: true } : {}),
      }),
    );
    const payload = res.payload;
    if (fetchReg.fulfilled.match(res) && payload?.result === 0) {
      openRegisteredClubAdmin(payload?.tenant?.subdomain || data.subdomain);
      return;
    }
    setBusy(false);
    dispatch(clearErr());
    if (payload?.code === "owner_exists") {
      setExistingEmail(data.login.trim().toLowerCase());
      return;
    }
    setError(errorText(payload, "Не удалось создать клуб. Попробуйте ещё раз."));
  };

  const loginHref = `/account/login?${new URLSearchParams({
    email: existingEmail,
    next: "/account",
  })}`;

  return (
    <div className={h.regPanel}>
      <ol className={h.steps}>
        {STEPS.map((label, i) => {
          const n = i + 1;
          const state = n === step ? h.stepOn : n < step ? h.stepDone : "";
          return (
            <li key={label} className={`${h.step} ${state}`}>
              <span>{n < step ? "✓" : n}</span>
              {label}
            </li>
          );
        })}
      </ol>

      <div className={h.formCard}>
        <div>
          <h3>До автоматизации клуба — всего 4 шага.</h3>
          <p>
            Заполните их прямо здесь — в конце сразу откроется кабинет вашего
            клуба.
          </p>
          <p className={h.formNote}>
            {TRIAL_DAYS} дней полного доступа • карта не нужна
          </p>
        </div>

        {existingEmail ? (
          <div className={h.form}>
            <p className={h.regInfo}>
              Аккаунт <b>{existingEmail}</b> уже зарегистрирован. Войдите в
              кабинет владельца — новый клуб можно добавить там.
            </p>
            <a href={loginHref} className={`${site.btn} ${site.btnLg}`}>
              Войти в кабинет
            </a>
            <button
              type="button"
              className={h.regBack}
              onClick={() => {
                setExistingEmail("");
                go(3);
              }}
            >
              Указать другой email
            </button>
          </div>
        ) : (
          <form className={h.form} onSubmit={step === 4 ? submit : next}>
            {step === 1 && (
              <>
                <label>
                  Название клуба
                  <input
                    value={data.clinicName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setData((d) => ({
                        ...d,
                        clinicName: value,
                        subdomain: subTouched ? d.subdomain : subdomainFromClubName(value),
                      }));
                    }}
                    placeholder="Например, Тигр"
                    autoComplete="organization"
                    maxLength={120}
                    autoFocus={false}
                  />
                </label>
                <p className={h.addr}>
                  Название увидят клиенты в приложении клуба
                </p>
              </>
            )}

            {step === 2 && (
              <>
                <label>
                  Адрес клуба
                  <input
                    value={data.subdomain}
                    onChange={(e) => {
                      setSubTouched(true);
                      patch("subdomain", e.target.value.replace(/\s/g, "").toLowerCase());
                    }}
                    placeholder="tigr"
                    autoComplete="off"
                    maxLength={32}
                  />
                </label>
                <p className={h.addr}>
                  Ваш адрес: <b>{hostLabel(data.subdomain)}</b>
                  <br />
                  Латиница, цифры и дефис
                </p>
              </>
            )}

            {step === 3 && (
              <>
                <label>
                  Ваше имя
                  <input
                    value={data.fullName}
                    onChange={(e) => patch("fullName", e.target.value)}
                    placeholder="Иван Иванов"
                    autoComplete="name"
                  />
                </label>
                <div className={h.formRow}>
                  <label>
                    Email
                    <input
                      type="email"
                      value={data.login}
                      onChange={(e) => patch("login", e.target.value)}
                      placeholder="you@club.ru"
                      autoComplete="email"
                    />
                  </label>
                  <label>
                    Телефон (необязательно)
                    <input
                      type="tel"
                      inputMode="tel"
                      value={formatPhoneDisplay(data.phone)}
                      onChange={(e) =>
                        patch(
                          "phone",
                          e.target.value.replace(/\D/g, "")
                            ? normalizePhoneRu(e.target.value)
                            : "",
                        )
                      }
                      placeholder="+7 (___) ___-__-__"
                      autoComplete="tel"
                    />
                  </label>
                </div>
                <label>
                  Пароль
                  <input
                    type="password"
                    value={data.password}
                    onChange={(e) => patch("password", e.target.value)}
                    placeholder="Минимум 8 символов"
                    autoComplete="new-password"
                    minLength={8}
                  />
                </label>
              </>
            )}

            {step === 4 && (
              <>
                <dl className={h.regReview}>
                  <div>
                    <dt>Клуб</dt>
                    <dd>{data.clinicName}</dd>
                  </div>
                  <div>
                    <dt>Адрес</dt>
                    <dd>{hostLabel(data.subdomain)}</dd>
                  </div>
                  <div>
                    <dt>Владелец</dt>
                    <dd>
                      {data.fullName} · {data.login}
                    </dd>
                  </div>
                </dl>
                {LEGAL_PUBLIC_ENABLED ? (
                  <div className={h.regConsents}>
                    <ConsentCheckbox
                      id="landing-accept-offer"
                      checked={acceptTerms}
                      onChange={setAcceptTerms}
                      disabled={busy}
                    >
                      Принимаю{" "}
                      <LegalDocLink doc="offer">оферту</LegalDocLink>
                      {" и "}
                      <LegalDocLink doc="payments">условия платежей</LegalDocLink>
                    </ConsentCheckbox>
                    <ConsentCheckbox
                      id="landing-consent-pd"
                      checked={consentPd}
                      onChange={setConsentPd}
                      disabled={busy}
                    >
                      Даю{" "}
                      <LegalDocLink doc="ownerConsent">
                        согласие на обработку персональных данных
                      </LegalDocLink>
                    </ConsentCheckbox>
                  </div>
                ) : null}
              </>
            )}

            {error ? <p className={h.regError}>{error}</p> : null}

            <div className={h.regActions}>
              <button
                type="submit"
                className={`${site.btn} ${site.btnLg}`}
                disabled={!valid || busy}
              >
                {step === 4
                  ? busy
                    ? "Создаём клуб…"
                    : "Создать клуб и открыть кабинет"
                  : busy
                    ? "Проверяем…"
                    : "Продолжить"}
              </button>
              {step > 1 ? (
                <button
                  type="button"
                  className={h.regBack}
                  onClick={() => go(step - 1)}
                  disabled={busy}
                >
                  ← Назад
                </button>
              ) : null}
            </div>

            <p className={h.consent}>
              Уже зарегистрированы? <Link to="/account/login">Войти в кабинет</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
